import { PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';
import { FrequencyType } from 'containers/ScheduleDetails/utils/rruleUtils';
import { UuidableName } from 'utils/api/types';

export enum SchedulePauseReason {
  MANUAL = 'manual',
  NO_ACTIVE_ASSIGNEES = 'no_active_assignees',
  TEMPLATE_ARCHIVED = 'template_archived',
}

export interface ScheduleGroup {
  uuid: string;
  name: string;
  memberCount: number;
}

export interface ScheduleDetailsResponse {
  uuid: string;
  name: string;
  createdAt: string;
  createdBy: UuidableName;
  updatedAt: string;
  template: UuidableName;
  // Non-null only on the entity path — the only way to tell which source a schedule uses.
  submissionForm: UuidableName | null;
  assignedTo: UuidableName[];
  assignedGroups?: ScheduleGroup[];
  // Read-only, detail read only: who actually gets occurrences.
  performers?: UuidableName[];
  recurrence: string;
  nextStart: string | null;
  nextDue: string | null;
  site: UuidableName | null;
  asset: UuidableName | null;
  contact: UuidableName | null;
  duration: string;
  isPaused: boolean;
  pauseReason: SchedulePauseReason | null;
  pausedAt: string | null;
  pausedBy: UuidableName | null;
  pauseContext: SchedulePauseContext | null;
  templateIsActive: boolean;
  // The zone the schedule's times read in; the organisation's when the schedule has none of its own.
  timezone: string;
  timezoneIsDefault: boolean;
}

export interface SchedulePauseContext {
  assigneeName?: string | null;
  assigneeUuid?: string | null;
  templateName?: string | null;
  templateUuid?: string | null;
  groupNames?: string[] | null;
  groupUuids?: string[] | null;
}

export type ScheduleSource = 'scheduled' | 'ad_hoc';

// Mutually exclusive form sources: the backend rejects both or neither.
export interface CreateScheduleData {
  name: string;
  template?: { uuid: string };
  submissionForm?: { uuid: string };
  assignedTo: { uuid: string }[];
  assignedGroups?: { uuid: string }[];
  recurrence: string;
  duration: string;
  site?: { uuid: string } | null;
  asset?: { uuid: string } | null;
  contact?: { uuid: string } | null;
  source?: ScheduleSource;
  timezone?: string;
}

export interface UpdateScheduleData {
  name?: string;
  template?: { uuid: string };
  submissionForm?: { uuid: string };
  assignedTo?: { uuid: string }[];
  assignedGroups?: { uuid: string }[];
  recurrence?: string;
  site?: { uuid: string } | null;
  asset?: { uuid: string } | null;
  contact?: { uuid: string } | null;
  duration?: string;
  resume?: boolean;
}

export interface ScheduleFormValues {
  name: string;
  template: UuidableName | null;
  // One ordered list of users and groups, so a chip stays where the user added it. Split back into
  // `assignedTo` / `assignedGroups` at submit.
  assignees: PickerValue[];
  site: UuidableName | null;
  asset: UuidableName | null;
  contact: UuidableName | null;
  startDate: Date | null;
  dueDate: Date | null;
  frequency: FrequencyType;
  timezone: string | null;
}

export interface ScheduleInstanceResponse {
  uuid: string;
  type: 'audit' | 'task';
  startDate: string;
  dueDate: string;
  template: UuidableName;
  templateIsActive: boolean;
  assignedTo: UuidableName;
  schedule: UuidableName;
  status: ScheduleStatuses;
  reason: string | null;
  inspection: UuidableName | null;
  submissionForm: UuidableName | null;
  site?: UuidableName | null;
  asset?: UuidableName | null;
  contact?: UuidableName | null;
}

export interface UpdateScheduleInstanceData {
  status?: string;
  reason?: string;
  assignedTo?: { uuid: string };
}

export enum ScheduleStatuses {
  OVERDUE = 'overdue',
  TODO = 'todo',
  IN_PROGRESS = 'in_progress',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  WONT_DO = 'wont_do',
}

export interface AuditTaskListResponse {
  results: ScheduleInstanceResponse[];
  count: number | null;
}

export interface PreviewCyclesData {
  recurrence: string;
  duration: string;
  count?: number;
}

export interface PreviewCycleResponse {
  start: string;
  due: string;
}

export interface PreviewAssigneeGroupsData {
  assignedGroups: string[];
}

export interface PreviewAssigneeGroupsResponse {
  total: number;
  licensed: number;
  skipped: { uuid: string; name: string | null; reason: string }[];
}
